import React, { useCallback, useEffect, useState } from 'react'
import {
  Box,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Paper,
  TextField,
  Tooltip,
  Typography
} from '@material-ui/core'
import { Alert } from '@material-ui/lab'
import { makeStyles } from '@material-ui/core/styles'
import { CheckCircle, ExpandLess, ExpandMore, Schedule, Visibility, VerifiedUser } from '@material-ui/icons'
import moment from 'moment'
import { useDispatch } from 'react-redux'
import Button from 'components/shared/Button'
import PdfModal from 'components/Applicants/partial/Dialog/PdfModal'
import SignaturePreview from 'components/Profile/partial/SignaturePreview'
import { getMySignature } from 'actions/user'
import { creditMemoDraftFileName, creditMemoVersionFileName } from 'tools/creditMemoFileName'
import { Toast } from 'components/shared/Toaster'
import AuthService from 'tools/AuthService'
import strings from 'strings'
import {
  approveCreditMemoVersion,
  downloadCreditMemoPdf,
  downloadCreditMemoVersionPdf,
  finalizeCreditMemoVersion,
  getCreditMemoApprovers,
  getCreditMemoPdf,
  getCreditMemoVersionPdf,
  getCreditMemoApproval,
  requestCreditMemoApproval,
  returnCreditMemoVersion,
  updateApplicationStatus,
  withdrawCreditMemoVersion
} from 'actions/applicants'

const S = strings.APPLICANT.CREDIT_MEMO.APPROVAL
// The application status the "approve the application too?" prompt sets (same value as the status dropdown).
const APPLICATION_APPROVED = 'Approved'
const RETURN_COMMENT_MAX = 2000
// Longer approver lists get a search box.
const APPROVER_SEARCH_MIN = 7
// How often a memo waiting on others is re-read, so signatures from other people show up.
const REFRESH_MS = 30000

const STATE_CHIP = {
  DRAFT: { label: S.STATE_DRAFT, color: '#56625F', background: '#F2F0EA' },
  IN_APPROVAL: { label: S.STATE_IN_APPROVAL, color: '#8F4F00', background: '#FDF1DF' },
  SIGNED: { label: S.STATE_SIGNED, color: '#1F4E79', background: '#E6F0FA' },
  APPROVED: { label: S.STATE_APPROVED, color: '#23613C', background: '#E7F2EA' },
  RETURNED: { label: S.STATE_RETURNED, color: '#A3251A', background: '#FCEBE8' },
  WITHDRAWN: { label: S.STATE_WITHDRAWN, color: '#56625F', background: '#F2F0EA' }
}

const useStyles = makeStyles((theme) => ({
  card: {
    marginBottom: theme.spacing(3),
    border: '1px solid #e0e0e0',
    borderRadius: 12
  },
  body: {
    padding: theme.spacing(2.5, 3)
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    flexWrap: 'wrap'
  },
  title: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    fontWeight: 600,
    fontSize: '1.1rem'
  },
  actions: {
    display: 'flex',
    gap: theme.spacing(1),
    flexWrap: 'wrap'
  },
  alert: {
    marginTop: theme.spacing(2)
  },
  comment: {
    marginTop: theme.spacing(1),
    whiteSpace: 'pre-wrap',
    fontStyle: 'italic'
  },
  historyToggle: {
    marginTop: theme.spacing(2),
    padding: 0,
    textTransform: 'none'
  },
  historyRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    padding: theme.spacing(1.25, 0),
    borderBottom: '1px solid #eee',
    '&:last-child': { borderBottom: 'none' }
  },
  historyMeta: {
    color: '#56625F',
    fontSize: '0.85rem'
  },
  waiting: {
    fontWeight: 600
  },
  signerList: {
    margin: theme.spacing(1, 0),
    padding: 0,
    listStyle: 'none'
  },
  signerRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(0.5, 0),
    flexWrap: 'wrap'
  },
  signerName: {
    fontWeight: 600,
    minWidth: 0,
    overflowWrap: 'anywhere'
  },
  signerIcon: {
    fontSize: '1.1rem'
  },
  approverSearch: {
    margin: theme.spacing(1, 0)
  },
  // Long lists scroll inside the dialog so the actions stay in view.
  approverList: {
    maxHeight: 320,
    overflowY: 'auto'
  },
  approverOption: {
    width: '100%',
    marginRight: 0,
    '& .MuiFormControlLabel-label': { minWidth: 0, overflowWrap: 'anywhere' }
  },
  approverSummary: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: theme.spacing(1),
    paddingTop: theme.spacing(1),
    borderTop: '1px solid #e0e0e0'
  },
  signaturePreview: {
    margin: theme.spacing(2, 0)
  },
  signaturePreviewHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    marginBottom: theme.spacing(1)
  }
}))

const formatDateTime = (value) => (value ? moment(value).format('MMM D, YYYY h:mm A') : '')

const StateChip = ({ state }) => {
  const config = STATE_CHIP[state] || STATE_CHIP.DRAFT
  return (
    <Chip
      size="small"
      label={config.label}
      style={{ color: config.color, backgroundColor: config.background, fontWeight: 600 }}
    />
  )
}

/**
 * One row per approver: signed (with when) or still waiting. Versions from before approvers were chosen only
 * have their signatures, so those are listed instead.
 */
const SignerList = ({ version, myUserId, classes }) => {
  const signatures = version?.signatures || []
  const signedAt = Object.fromEntries(signatures.map(s => [s.userId, s.signedAt]))
  const rows = version?.approvers?.length
    ? version.approvers.map(a => ({ userId: a.userId, name: a.name, signedAt: signedAt[a.userId] }))
    : signatures.map(s => ({ userId: s.userId, name: s.name, signedAt: s.signedAt }))
  if (!rows.length) return null
  return (
    <Box component="ul" className={classes.signerList}>
      {rows.map(row => (
        <Box component="li" key={row.userId} className={classes.signerRow}>
          {row.signedAt
            ? <CheckCircle className={classes.signerIcon} style={{ color: '#23613C' }} aria-hidden="true" />
            : <Schedule className={classes.signerIcon} style={{ color: '#8F4F00' }} aria-hidden="true" />}
          <span className={classes.signerName}>{row.userId === myUserId ? S.SIGNER_YOU(row.name) : row.name}</span>
          <span className={classes.historyMeta}>
            {row.signedAt ? S.SIGNER_SIGNED(formatDateTime(row.signedAt)) : S.SIGNER_WAITING}
          </span>
        </Box>
      ))}
    </Box>
  )
}

/**
 * Approval workflow for the credit memo: request, approve (which signs), return with a comment, withdraw,
 * and the history of frozen versions. Reports whether the memo is locked so the tab can disable editing.
 */
const CreditMemoApprovalPanel = ({ applicantId, businessName, memoReady, onLockChange }) => {
  const classes = useStyles()
  const [approval, setApproval] = useState(null)
  const [loadError, setLoadError] = useState(false)
  const [busy, setBusy] = useState(false)
  const [approveOpen, setApproveOpen] = useState(false)
  // The approver's own signature, loaded when the approve dialog opens so a change made meanwhile shows.
  const [mySignature, setMySignature] = useState(null)

  useEffect(() => {
    if (!approveOpen) return undefined
    let cancelled = false
    setMySignature(null)
    getMySignature().then(data => { if (!cancelled) setMySignature(data) }).catch(() => {})
    return () => { cancelled = true }
  }, [approveOpen])
  const [attested, setAttested] = useState(false)
  const [returnOpen, setReturnOpen] = useState(false)
  const [returnComment, setReturnComment] = useState('')
  const [historyOpen, setHistoryOpen] = useState(false)
  const [requestOpen, setRequestOpen] = useState(false)
  const [approverOptions, setApproverOptions] = useState([])
  const [loadingApprovers, setLoadingApprovers] = useState(false)
  const [selectedApprovers, setSelectedApprovers] = useState([])
  const [approverSearch, setApproverSearch] = useState('')
  // The PDF shown in the viewer: { title, load, download }, or null when closed.
  const [viewer, setViewer] = useState(null)
  const [finalizeOpen, setFinalizeOpen] = useState(false)
  // Offered after the requester approves the memo, while the application isn't approved yet.
  const [statusPromptOpen, setStatusPromptOpen] = useState(false)
  const dispatch = useDispatch()

  const previewDraft = () => setViewer({
    title: S.PREVIEW_PDF,
    load: () => getCreditMemoPdf(applicantId),
    download: () => downloadCreditMemoPdf(applicantId, creditMemoDraftFileName(businessName)).catch(() => {})
  })

  const previewVersion = (version) => setViewer({
    title: S.VERSION(version.versionNumber),
    load: () => getCreditMemoVersionPdf(applicantId, version.uuid),
    download: () => downloadCreditMemoVersionPdf(applicantId, version.uuid,
      version.pdfFileName || creditMemoVersionFileName(businessName, version.versionNumber, version.state === 'APPROVED')).catch(() => {})
  })

  // quiet: a background refresh keeps what is shown if it fails, instead of replacing it with the error.
  const load = useCallback(async (quiet = false) => {
    try {
      const data = await getCreditMemoApproval(applicantId)()
      setApproval(data)
      setLoadError(false)
      // Locked until the requester approves it, including once everyone has signed.
      onLockChange && onLockChange(data?.state === 'IN_APPROVAL' || data?.state === 'SIGNED')
    } catch (e) {
      if (!quiet) setLoadError(true)
    }
  }, [applicantId, onLockChange])

  useEffect(() => {
    if (applicantId) load()
  }, [applicantId, load])

  // While the memo waits on someone else (signatures, or the requester's approval), pick up their actions:
  // every REFRESH_MS while the tab is visible, and as soon as the user comes back to it.
  const waitingOnOthers = approval?.state === 'IN_APPROVAL' || approval?.state === 'SIGNED'
  useEffect(() => {
    if (!applicantId || !waitingOnOthers) return undefined
    const refresh = () => { if (document.visibilityState === 'visible') load(true) }
    const timer = setInterval(refresh, REFRESH_MS)
    document.addEventListener('visibilitychange', refresh)
    window.addEventListener('focus', refresh)
    return () => {
      clearInterval(timer)
      document.removeEventListener('visibilitychange', refresh)
      window.removeEventListener('focus', refresh)
    }
  }, [applicantId, waitingOnOthers, load])

  const run = async (action, successMessage) => {
    setBusy(true)
    try {
      const result = await action()
      Toast({ type: 'success', message: typeof successMessage === 'function' ? successMessage(result) : successMessage })
      return true
    } catch (e) {
      // The request helper already shows the backend's message.
      return false
    } finally {
      await load()
      setBusy(false)
    }
  }

  const active = approval?.activeVersion
  const openRequest = async () => {
    setRequestOpen(true)
    setSelectedApprovers([])
    setApproverSearch('')
    setLoadingApprovers(true)
    try {
      const options = await getCreditMemoApprovers(applicantId)() || []
      setApproverOptions([...options].sort((a, b) => (a.name || '').localeCompare(b.name || '')))
    } catch (e) {
      setApproverOptions([])
    } finally {
      setLoadingApprovers(false)
    }
  }

  const searchTerm = approverSearch.trim().toLowerCase()
  const visibleApprovers = searchTerm
    ? approverOptions.filter(a => `${a.name || ''} ${a.email || ''}`.toLowerCase().includes(searchTerm))
    : approverOptions
  const visibleSelected = visibleApprovers.filter(a => selectedApprovers.includes(a.userId)).length
  // Select all acts on the people shown, and keeps anyone already picked who is filtered out.
  const toggleAllVisible = (checked) => {
    const ids = visibleApprovers.map(a => a.userId)
    setSelectedApprovers(selected => checked
      ? [...new Set([...selected, ...ids])]
      : selected.filter(id => !ids.includes(id)))
  }

  const toggleApprover = (userId) => setSelectedApprovers(selected =>
    selected.includes(userId) ? selected.filter(id => id !== userId) : [...selected, userId])

  const handleRequest = async () => {
    const ok = await run(() => requestCreditMemoApproval(applicantId, selectedApprovers)(), S.REQUESTED_TOAST)
    if (ok) setRequestOpen(false)
  }
  const handleWithdraw = () => run(() => withdrawCreditMemoVersion(applicantId, active.uuid)(), S.WITHDRAWN_TOAST)

  const handleApprove = async () => {
    // The last signature hands the memo to the requester; any other leaves it waiting for more signatures.
    const toast = (version) => version?.state === 'SIGNED'
      ? S.ALL_SIGNED_TOAST
      : S.SIGNED_TOAST(Math.max((version?.requiredApprovals || 1) - (version?.signatures?.length || 0), 1))
    const ok = await run(() => approveCreditMemoVersion(applicantId, active.uuid)(), toast)
    if (ok) {
      setApproveOpen(false)
      setAttested(false)
    }
  }

  // The requester approves a memo everyone signed (creating the signed PDF), then may approve the application too.
  const handleFinalize = async () => {
    const ok = await run(() => finalizeCreditMemoVersion(applicantId, active.uuid)(), S.APPROVED_TOAST)
    setFinalizeOpen(false)
    if (ok && approval.applicationStatus !== APPLICATION_APPROVED) setStatusPromptOpen(true)
  }

  const handleApproveApplication = async () => {
    setBusy(true)
    try {
      await dispatch(updateApplicationStatus(applicantId, APPLICATION_APPROVED))
      setStatusPromptOpen(false)
      await load()
    } catch (e) {
      // The request helper already shows the backend's message.
    } finally {
      setBusy(false)
    }
  }

  const handleReturn = async () => {
    const ok = await run(() => returnCreditMemoVersion(applicantId, active.uuid, returnComment.trim())(), S.RETURNED_TOAST)
    if (ok) {
      setReturnOpen(false)
      setReturnComment('')
    }
  }

  if (loadError) {
    return (
      <Alert severity="error" className={classes.card} action={<Button size="small" variant="text" onClick={() => load()}>{S.RETRY}</Button>}>
        {S.LOAD_ERROR}
      </Alert>
    )
  }
  if (!approval) return null

  const state = approval.state
  const user = AuthService.loggedInUser
  // Who can still act, from the viewer's side: "you", "you or Morgan", or the chosen approvers by name.
  const myEmail = (user?.email || AuthService.getProfile()?.email || '').toLowerCase()
  const otherApprovers = (active?.approvers || []).filter(a => (a.email || '').toLowerCase() !== myEmail).map(a => a.name)
  const waitingLine = !active?.approvers?.length ? null
    : approval.canDecide
      ? (otherApprovers.length ? S.WAITING_FOR_YOU_OR(otherApprovers.join(', '), otherApprovers.length) : S.WAITING_FOR_YOU)
      : S.WAITING_FOR(active.approvers.map(a => a.name).join(', '), active.approvers.length)
  // Several required signatures: a progress line and one row per approver replace the single waiting line.
  const signatures = active?.signatures || []
  const required = active?.requiredApprovals || 1
  const multiSignature = required > 1
  const myUserId = (active?.approvers || []).find(a => (a.email || '').toLowerCase() === myEmail)?.userId
  const signerName = [user?.firstName, user?.lastName].filter(Boolean).join(' ')
  const requestDisabledReason = !memoReady ? S.MEMO_NOT_READY : !approval.approversAvailable ? S.NO_APPROVERS : null

  return (
    <Paper className={`${classes.card} no-print`} elevation={0}>
      <Box className={classes.body}>
        <Box className={classes.header}>
          <Box className={classes.title}>
            <VerifiedUser style={{ color: '#253837' }} />
            <span>{S.TITLE}</span>
            <StateChip state={state} />
          </Box>
          <Box className={classes.actions}>
            {state === 'DRAFT' && (
              <Button size="small" variant="outlined" startIcon={<Visibility />} onClick={previewDraft}>
                {S.PREVIEW_PDF}
              </Button>
            )}
            {approval.canRequestApproval && (
              <Tooltip title={requestDisabledReason || ''}>
                <span>
                  <Button size="small" onClick={openRequest} loading={busy} disabled={!!requestDisabledReason}>
                    {S.REQUEST_APPROVAL}
                  </Button>
                </span>
              </Tooltip>
            )}
            {(state === 'IN_APPROVAL' || state === 'SIGNED') && active && (
              <Button size="small" variant="outlined" startIcon={<Visibility />} onClick={() => previewVersion(active)}>
                {S.VIEW_MEMO_PDF}
              </Button>
            )}
            {approval.canApprove && (
              <Button size="small" onClick={() => setFinalizeOpen(true)} disabled={busy}>
                {S.FINALIZE}
              </Button>
            )}
            {approval.canDecide && (
              <>
                <Button size="small" variant="outlined" onClick={() => setReturnOpen(true)} disabled={busy}>
                  {S.RETURN}
                </Button>
                <Button size="small" onClick={() => setApproveOpen(true)} disabled={busy}>
                  {S.APPROVE}
                </Button>
              </>
            )}
            {approval.canWithdraw && (
              <Button size="small" variant="outlined" onClick={handleWithdraw} loading={busy}>
                {S.WITHDRAW}
              </Button>
            )}
            {state === 'APPROVED' && active && (
              <Button
                size="small"
                variant="outlined"
                startIcon={<Visibility />}
                onClick={() => previewVersion(active)}
              >
                {S.VIEW_SIGNED}
              </Button>
            )}
          </Box>
        </Box>

        {state === 'DRAFT' && (
          <Typography className={classes.historyMeta} style={{ marginTop: 12 }}>
            {S.DRAFT_DESCRIPTION}
            {approval.canRequestApproval && !approval.approversAvailable ? ` ${S.NO_APPROVERS}` : ''}
          </Typography>
        )}
        {state === 'IN_APPROVAL' && active && (
          <Alert severity="info" className={classes.alert}>
            <div>
              {approval.canWithdraw
                ? S.REQUESTED_BY_YOU(formatDateTime(active.requestedAt))
                : S.REQUESTED_BY(active.requestedBy, formatDateTime(active.requestedAt))}
            </div>
            {multiSignature ? (
              <>
                <div className={classes.waiting}>{S.SIGNATURES_PROGRESS(signatures.length, required)}</div>
                <SignerList version={active} myUserId={myUserId} classes={classes} />
              </>
            ) : waitingLine && <div className={classes.waiting}>{waitingLine}</div>}
            <div className={classes.historyMeta}>{active.approvers?.length ? S.WAITING_LOCKED : S.WAITING}</div>
          </Alert>
        )}
        {state === 'SIGNED' && active && (
          <Alert severity="success" className={classes.alert}>
            <div className={classes.waiting}>{S.ALL_SIGNED(signatures.length)}</div>
            <SignerList version={active} myUserId={myUserId} classes={classes} />
            <div className={classes.historyMeta}>{approval.canApprove ? S.READY_FOR_YOU : S.READY_TO_APPROVE}</div>
          </Alert>
        )}
        {state === 'APPROVED' && active && (
          <Alert severity="success" className={classes.alert}>
            <div>{S.APPROVED_ON_BY(active.decidedBy, formatDateTime(active.decidedAt))}</div>
            <SignerList version={active} myUserId={myUserId} classes={classes} />
          </Alert>
        )}
        {state === 'RETURNED' && active && (
          <Alert severity="warning" className={classes.alert}>
            <div>{S.RETURNED_BY(active.decidedBy, formatDateTime(active.decidedAt))}</div>
            <div className={classes.comment}>“{active.returnComment}”</div>
            {signatures.length > 0 && (
              <div className={classes.historyMeta}>{S.SIGNATURES_SET_ASIDE(signatures.map(s => s.name).join(', '))}</div>
            )}
          </Alert>
        )}

        {approval.versions?.length > 0 && (
          <>
            <Button
              variant="text"
              size="small"
              className={classes.historyToggle}
              onClick={() => setHistoryOpen(open => !open)}
              endIcon={historyOpen ? <ExpandLess /> : <ExpandMore />}
            >
              {S.HISTORY(approval.versions.length)}
            </Button>
            <Collapse in={historyOpen}>
              <Box>
                {approval.versions.map(version => (
                  <Box key={version.uuid} className={classes.historyRow}>
                    <Box>
                      <Box style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <Typography style={{ fontWeight: 600 }}>{S.VERSION(version.versionNumber)}</Typography>
                        <StateChip state={version.state} />
                        {!version.active && version.state !== 'WITHDRAWN' && (
                          <Typography className={classes.historyMeta}>{S.SUPERSEDED}</Typography>
                        )}
                      </Box>
                      <Typography className={classes.historyMeta}>
                        {S.HISTORY_REQUESTED(version.requestedBy, formatDateTime(version.requestedAt))}
                        {version.decidedBy ? ` · ${S.HISTORY_DECIDED(version.decidedBy, formatDateTime(version.decidedAt))}` : ''}
                      </Typography>
                      {version.returnComment && (
                        <Typography className={`${classes.historyMeta} ${classes.comment}`}>“{version.returnComment}”</Typography>
                      )}
                    </Box>
                    <Button
                      size="small"
                      variant="text"
                      startIcon={<Visibility />}
                      onClick={() => previewVersion(version)}
                    >
                      {S.VIEW_PDF}
                    </Button>
                  </Box>
                ))}
              </Box>
            </Collapse>
          </>
        )}
      </Box>

      <Dialog open={requestOpen} onClose={() => !busy && setRequestOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{S.REQUEST_TITLE}</DialogTitle>
        <DialogContent>
          <Typography gutterBottom>{S.REQUEST_BODY}</Typography>
          {loadingApprovers ? (
            <Box display="flex" justifyContent="center" p={2}><CircularProgress size={24} /></Box>
          ) : approverOptions.length === 0 ? (
            <Alert severity="warning">{S.NO_APPROVERS}</Alert>
          ) : (
            <>
              {approverOptions.length >= APPROVER_SEARCH_MIN && (
                <TextField
                  value={approverSearch}
                  onChange={e => setApproverSearch(e.target.value)}
                  placeholder={S.SEARCH_APPROVERS}
                  variant="outlined"
                  size="small"
                  fullWidth
                  autoFocus
                  className={classes.approverSearch}
                  inputProps={{ 'aria-label': S.SEARCH_APPROVERS }}
                />
              )}
              {visibleApprovers.length > 1 && (
                <FormControlLabel
                  control={
                    <Checkbox
                      color="primary"
                      checked={visibleSelected === visibleApprovers.length}
                      indeterminate={visibleSelected > 0 && visibleSelected < visibleApprovers.length}
                      onChange={e => toggleAllVisible(e.target.checked)}
                    />
                  }
                  label={<strong>{searchTerm ? S.SELECT_ALL_SHOWN(visibleApprovers.length) : S.SELECT_ALL}</strong>}
                />
              )}
              <Box className={classes.approverList}>
                {visibleApprovers.length === 0 && (
                  <Typography className={classes.historyMeta}>{S.NO_APPROVER_MATCH}</Typography>
                )}
                {visibleApprovers.map(option => (
                  <Box key={option.userId}>
                    <FormControlLabel
                      className={classes.approverOption}
                      control={
                        <Checkbox
                          color="primary"
                          checked={selectedApprovers.includes(option.userId)}
                          onChange={() => toggleApprover(option.userId)}
                        />
                      }
                      label={
                        <span>
                          {option.name}
                          <span className={classes.historyMeta}> · {option.email}</span>
                        </span>
                      }
                    />
                  </Box>
                ))}
              </Box>
              {selectedApprovers.length > 0 && (
                <Box className={classes.approverSummary}>
                  <Typography className={classes.historyMeta}>
                    {[
                      S.APPROVERS_SELECTED(selectedApprovers.length),
                      S.ALL_MUST_SIGN(selectedApprovers.length)
                    ].filter(Boolean).join(' · ')}
                  </Typography>
                  <Button size="small" variant="text" onClick={() => setSelectedApprovers([])}>{S.CLEAR_SELECTION}</Button>
                </Box>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button variant="text" onClick={() => setRequestOpen(false)} disabled={busy}>{S.CANCEL}</Button>
          <Button onClick={handleRequest} disabled={selectedApprovers.length === 0} loading={busy}>
            {S.SEND_REQUEST(selectedApprovers.length)}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={approveOpen} onClose={() => !busy && setApproveOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{S.APPROVE_TITLE(active?.versionNumber)}</DialogTitle>
        <DialogContent>
          <Typography gutterBottom>{S.APPROVE_BODY(signerName)}</Typography>
          {mySignature && (
            <Box className={classes.signaturePreview}>
              <Box className={classes.signaturePreviewHeader}>
                <Typography className={classes.historyMeta}>{strings.PROFILE_SIGNATURE.APPROVE_PREVIEW}</Typography>
                <a href="/profile?tab=signature" target="_blank" rel="noopener noreferrer">{strings.PROFILE_SIGNATURE.APPROVE_CHANGE}</a>
              </Box>
              <SignaturePreview
                compact
                name={mySignature.signerName}
                email={mySignature.signerEmail}
                mark={mySignature.mark === 'DRAWN' && mySignature.drawnImage ? 'DRAWN' : 'TYPED'}
                stamp={mySignature.stamp}
                drawnImage={mySignature.drawnImage}
              />
            </Box>
          )}
          <FormControlLabel
            control={<Checkbox checked={attested} onChange={e => setAttested(e.target.checked)} color="primary" />}
            label={S.ATTEST}
          />
        </DialogContent>
        <DialogActions>
          <Button variant="text" onClick={() => setApproveOpen(false)} disabled={busy}>{S.CANCEL}</Button>
          <Button onClick={handleApprove} disabled={!attested} loading={busy}>{S.APPROVE_CONFIRM}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={returnOpen} onClose={() => !busy && setReturnOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{S.RETURN_TITLE(active?.versionNumber)}</DialogTitle>
        <DialogContent>
          <Typography gutterBottom>{S.RETURN_BODY}</Typography>
          <TextField
            label={S.RETURN_COMMENT}
            value={returnComment}
            onChange={e => setReturnComment(e.target.value)}
            multiline
            rows={4}
            fullWidth
            required
            variant="outlined"
            inputProps={{ maxLength: RETURN_COMMENT_MAX }}
            helperText={`${returnComment.length}/${RETURN_COMMENT_MAX}`}
          />
        </DialogContent>
        <DialogActions>
          <Button variant="text" onClick={() => setReturnOpen(false)} disabled={busy}>{S.CANCEL}</Button>
          <Button onClick={handleReturn} disabled={!returnComment.trim()} loading={busy}>{S.RETURN_CONFIRM}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={finalizeOpen} onClose={() => !busy && setFinalizeOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{S.FINALIZE_TITLE(active?.versionNumber)}</DialogTitle>
        <DialogContent>
          <Typography>{S.FINALIZE_BODY(signatures.length)}</Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="text" onClick={() => setFinalizeOpen(false)} disabled={busy}>{S.CANCEL}</Button>
          <Button onClick={handleFinalize} loading={busy}>{S.FINALIZE_CONFIRM}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={statusPromptOpen} onClose={() => !busy && setStatusPromptOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{S.STATUS_PROMPT_TITLE}</DialogTitle>
        <DialogContent>
          <Typography>
            {approval.applicationStatus ? S.STATUS_PROMPT_BODY(approval.applicationStatus) : S.STATUS_PROMPT_BODY_NO_STATUS}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="text" onClick={() => setStatusPromptOpen(false)} disabled={busy}>{S.STATUS_PROMPT_KEEP}</Button>
          <Button onClick={handleApproveApplication} loading={busy}>{S.STATUS_PROMPT_CONFIRM}</Button>
        </DialogActions>
      </Dialog>

      <PdfModal
        open={!!viewer}
        onClose={() => setViewer(null)}
        title={viewer?.title}
        loadPdf={viewer?.load}
        onDownload={viewer?.download}
      />
    </Paper>
  )
}

export default CreditMemoApprovalPanel
